import { mergePresentationOptions, resolvePresentationMode, validateAlpha, validateBlur, validateColor, validateDimension, validateZIndex, } from '../shared.js';
import { widgetBaseStyles } from './widget-styles.js';
export class WidgetShell {
    constructor(presentationOptions, callbacks) {
        this.iframeUrl = '';
        this.isOpen = false;
        this.elements = null;
        this.options = mergePresentationOptions(undefined, presentationOptions);
        this.callbacks = callbacks;
        this.messageHandler = (event) => this.handleMessage(event);
        this.keydownHandler = (event) => {
            if (event.key === 'Escape' && this.isOpen) {
                this.close();
            }
        };
    }
    mount(iframeUrl) {
        this.iframeUrl = iframeUrl;
        if (!WidgetShell.hasInjectedBaseStyles) {
            const style = document.createElement('style');
            style.id = 'pillow-sdk-widget-base-styles';
            style.textContent = widgetBaseStyles;
            document.head.appendChild(style);
            WidgetShell.hasInjectedBaseStyles = true;
        }
        if (!this.elements) {
            this.createWidget();
            window.addEventListener('message', this.messageHandler);
            document.addEventListener('keydown', this.keydownHandler);
        }
        this.elements?.iframe.setAttribute('src', iframeUrl);
        this.applyLayout();
    }
    setOptions(presentationOptions) {
        this.options = mergePresentationOptions(this.options, presentationOptions);
        if (this.elements) {
            this.applyLayout();
        }
    }
    reload(iframeUrl) {
        this.iframeUrl = iframeUrl;
        if (!this.elements) {
            this.mount(iframeUrl);
            return;
        }
        this.elements.iframe.setAttribute('src', iframeUrl);
    }
    open() {
        if (!this.elements)
            return;
        if (!this.isOpen) {
            void this.elements.container.offsetWidth;
            void this.elements.chatWindow.offsetWidth;
        }
        this.isOpen = true;
        this.elements.container.classList.add('is-open');
        this.elements.chatWindow.classList.add('open');
        this.postMessageToChat({ type: 'pillow:opened' });
        this.callbacks.onOpen();
    }
    close() {
        if (!this.elements)
            return;
        this.isOpen = false;
        this.elements.container.classList.remove('is-open');
        this.elements.chatWindow.classList.remove('open');
        this.postMessageToChat({ type: 'pillow:closed' });
        this.callbacks.onClose();
    }
    toggle() {
        if (this.isOpen) {
            this.close();
            return;
        }
        this.open();
    }
    isVisible() {
        return this.isOpen;
    }
    requestClearSession() {
        this.postMessageToChat({ type: 'pillow:clearSession' });
    }
    destroy() {
        window.removeEventListener('message', this.messageHandler);
        document.removeEventListener('keydown', this.keydownHandler);
        this.elements?.container.remove();
        this.elements = null;
        this.isOpen = false;
    }
    createWidget() {
        const container = document.createElement('div');
        container.className = 'pillow-container position-right';
        const backdrop = document.createElement('div');
        backdrop.className = 'pillow-backdrop';
        const chatWindow = document.createElement('div');
        chatWindow.className = 'pillow-window';
        const windowClose = document.createElement('button');
        windowClose.className = 'pillow-window-close';
        windowClose.setAttribute('aria-label', 'Close chat');
        windowClose.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
      </svg>
    `;
        const iframe = document.createElement('iframe');
        iframe.className = 'pillow-iframe';
        iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms');
        iframe.setAttribute('allow', 'camera; microphone; fullscreen');
        iframe.setAttribute('title', 'Pillow Study');
        chatWindow.appendChild(iframe);
        chatWindow.appendChild(windowClose);
        container.appendChild(backdrop);
        container.appendChild(chatWindow);
        const target = this.resolveTarget();
        target.appendChild(container);
        this.elements = {
            container,
            backdrop,
            chatWindow,
            windowClose,
            iframe,
        };
        backdrop.addEventListener('click', () => this.close());
        windowClose.addEventListener('click', () => this.close());
    }
    applyLayout() {
        if (!this.elements)
            return;
        const options = mergePresentationOptions(undefined, this.options);
        const mode = resolvePresentationMode(options);
        const positionClass = options.position === 'bottom-left' ? 'position-left' : 'position-right';
        const classNames = [
            'pillow-container',
            positionClass,
            `presentation-${mode}`,
            mode === 'embed' ? 'embedded' : '',
            this.isOpen ? 'is-open' : '',
        ].filter(Boolean);
        this.elements.container.className = classNames.join(' ');
        this.elements.container.style.zIndex = String(validateZIndex(options.zIndex, 999999));
        if (mode === 'modal') {
            this.elements.container.style.top = '0';
            this.elements.container.style.right = '0';
            this.elements.container.style.bottom = '0';
            this.elements.container.style.left = '0';
        }
        else if (mode !== 'embed') {
            this.elements.container.style.bottom = resolveOffset(options.bottom, '20px');
            this.elements.container.style.top = resolveOffset(options.top, '');
            this.elements.container.style.left = resolveOffset(options.left, '');
            this.elements.container.style.right = resolveOffset(options.right, '');
        }
        const scale = options.scale ?? 1;
        if (mode === 'embed' && scale !== 1) {
            this.elements.container.style.transform = `scale(${scale})`;
            this.elements.container.style.transformOrigin = 'top left';
            this.elements.container.style.width = `${100 / scale}%`;
            this.elements.container.style.height = `${100 / scale}%`;
        }
        const defaultWidth = mode === 'modal' ? '400px' : '360px';
        const defaultHeight = mode === 'overlay' ? '540px' : mode === 'modal' ? '680px' : '520px';
        const defaultBorderRadius = mode === 'overlay' ? '20px' : '24px';
        const safeWidth = validateDimension(options.width, defaultWidth);
        const safeHeight = validateDimension(options.height, defaultHeight);
        const safeBorderRadius = validateDimension(options.borderRadius, defaultBorderRadius);
        this.elements.backdrop.style.setProperty('--pillow-backdrop-color', validateColor(options.backdrop?.color, '#0f172a'));
        this.elements.backdrop.style.setProperty('--pillow-backdrop-alpha', String(validateAlpha(options.backdrop?.alpha, 0.42)));
        this.elements.backdrop.style.setProperty('--pillow-backdrop-blur', `${validateBlur(options.backdrop?.blur, 6)}px`);
        if (mode !== 'embed') {
            this.elements.chatWindow.style.width = safeWidth;
            this.elements.chatWindow.style.height = safeHeight;
        }
        this.elements.chatWindow.style.bottom = mode === 'modal' ? '' : '0';
        this.elements.chatWindow.style.borderRadius = safeBorderRadius;
        this.elements.chatWindow.style.transformOrigin =
            mode === 'modal' ? 'center center' : options.position === 'bottom-left' ? 'bottom left' : 'bottom right';
        if (mode !== 'modal') {
            this.elements.chatWindow.style.transform = '';
        }
        this.elements.iframe.style.borderRadius = safeBorderRadius;
        this.elements.windowClose.style.display = mode === 'embed' ? 'none' : '';
    }
    handleMessage(event) {
        if (!this.elements || !this.iframeUrl) {
            return;
        }
        try {
            const expectedOrigin = new URL(this.iframeUrl).origin;
            if (event.origin !== expectedOrigin) {
                return;
            }
        }
        catch {
            return;
        }
        const payload = event.data;
        if (!payload?.type) {
            return;
        }
        switch (payload.type) {
            case 'pillow:close':
                this.close();
                break;
            default:
                break;
        }
        this.callbacks.onBridgeMessage(payload);
    }
    postMessageToChat(message) {
        if (!this.elements?.iframe.contentWindow) {
            return;
        }
        try {
            const origin = new URL(this.iframeUrl).origin;
            this.elements.iframe.contentWindow.postMessage(message, origin);
        }
        catch {
            // ignore
        }
    }
    resolveTarget() {
        if (resolvePresentationMode(this.options) === 'embed' && this.options.target) {
            if (typeof this.options.target === 'string') {
                const match = document.querySelector(this.options.target);
                if (match instanceof HTMLElement) {
                    return match;
                }
            }
            else if (this.options.target instanceof HTMLElement) {
                return this.options.target;
            }
        }
        return document.body;
    }
}
WidgetShell.hasInjectedBaseStyles = false;
function resolveOffset(value, fallback) {
    if (value === undefined)
        return fallback;
    return typeof value === 'number' ? `${value}px` : value;
}
//# sourceMappingURL=widget-shell.js.map